import { useState, useEffect, useCallback, useRef } from 'react';
import { chatRepository } from '../../../services/repositories/ChatRepository';

export interface ConversationCounters {
    unassigned: number;
    mine: number;
    all: number;
}

const EMPTY: ConversationCounters = { unassigned: 0, mine: 0, all: 0 };
const CACHE_PREFIX = 'adlitics:chat-tab-counters:';
const FETCH_COOLDOWN_MS = 5_000;

function scopeKey(organizationId: string, userId?: string, connectionId?: string): string {
    return `${organizationId}:${userId ?? ''}:${connectionId ?? ''}`;
}

function readCache(key: string): ConversationCounters | null {
    try {
        const raw = sessionStorage.getItem(CACHE_PREFIX + key);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<ConversationCounters>;
        if (
            typeof parsed.unassigned !== 'number'
            || typeof parsed.mine !== 'number'
            || typeof parsed.all !== 'number'
        ) {
            return null;
        }
        return { unassigned: parsed.unassigned, mine: parsed.mine, all: parsed.all };
    } catch {
        return null;
    }
}

function writeCache(key: string, counters: ConversationCounters): void {
    try {
        sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify(counters));
    } catch {
        /* quota / private mode */
    }
}

interface UseConversationCountersOptions {
    organizationId?: string;
    userId?: string;
    connectionId?: string;
    enabled?: boolean;
}

/** Contadores org-wide dos tabs Não atribuído / Meus / Tudo. */
export function useConversationCounters({
    organizationId,
    userId,
    connectionId,
    enabled = true,
}: UseConversationCountersOptions) {
    const key = organizationId ? scopeKey(organizationId, userId, connectionId) : '';
    const [counters, setCounters] = useState<ConversationCounters>(
        () => (key ? readCache(key) ?? EMPTY : EMPTY),
    );
    const lastFetchRef = useRef(0);
    const scopeRef = useRef(key);

    // Troca de org/conexão: rehidrata cache daquele escopo sem piscar 0.
    if (key !== scopeRef.current) {
        scopeRef.current = key;
        setCounters(key ? readCache(key) ?? EMPTY : EMPTY);
        lastFetchRef.current = 0;
    }

    const fetchCounters = useCallback(async () => {
        if (!enabled || !organizationId || !key) return;

        const now = Date.now();
        if (now - lastFetchRef.current < FETCH_COOLDOWN_MS) return;
        lastFetchRef.current = now;

        try {
            const result = await chatRepository.getConversationCounters(
                organizationId,
                userId,
                connectionId,
            );
            writeCache(key, result);
            setCounters(result);
        } catch (error) {
            console.error('[useConversationCounters] Erro:', error);
        }
    }, [organizationId, userId, connectionId, enabled, key]);

    useEffect(() => {
        lastFetchRef.current = 0;
        void fetchCounters();
    }, [fetchCounters]);

    useEffect(() => {
        const handleRefresh = () => {
            lastFetchRef.current = 0;
            void fetchCounters();
        };

        window.addEventListener('chat:message_received', handleRefresh);
        window.addEventListener('assignment:changed', handleRefresh);
        window.addEventListener('conversation:meta_changed', handleRefresh);

        return () => {
            window.removeEventListener('chat:message_received', handleRefresh);
            window.removeEventListener('assignment:changed', handleRefresh);
            window.removeEventListener('conversation:meta_changed', handleRefresh);
        };
    }, [fetchCounters]);

    return {
        counters,
        refreshCounters: fetchCounters,
    };
}
